import React, { useEffect, useState, useRef } from 'react';
import { css } from '@emotion/react';
import { createPortal } from 'react-dom';
import { lighten } from 'polished';
import { colors, fontStyles, spacing } from '../../styles';
import { appStoreUrl, playStoreUrl } from '../../constants';

type Platform = 'ios' | 'android' | 'web';

interface AppUpdateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  platform?: Platform;
}

const AppUpdateDialog: React.FC<AppUpdateDialogProps> = ({ isOpen, onClose, platform = 'ios' }) => {
  const [isDarkMode, setIsDarkMode] = useState(document.documentElement.getAttribute('data-theme') === 'dark');
  const updateButtonRef = useRef<HTMLButtonElement>(null);

  // Handle theme changes
  useEffect(() => {
    const handleThemeChange = () => {
      setIsDarkMode(document.documentElement.getAttribute('data-theme') === 'dark');
    };

    handleThemeChange();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          handleThemeChange();
        }
      });
    });

    observer.observe(document.documentElement, { attributes: true });

    return () => observer.disconnect();
  }, []);

  // Prevent scrolling of background content when dialog is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Handle Escape key to dismiss dialog
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Focus the Update button when dialog opens
  useEffect(() => {
    if (isOpen && updateButtonRef.current) {
      updateButtonRef.current.focus();
    }
  }, [isOpen]);

  const handleUpdateNow = () => {
    const storeUrl = platform === 'android' ? playStoreUrl : appStoreUrl;
    window.open(storeUrl, '_blank');
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="update-dialog-title"
      css={css`
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 1000;
        overflow: hidden;
        padding: ${spacing.deci};
      `}
    >
      <div
        css={css`
          ${isDarkMode ? `background-color: ${lighten(0.1, colors.liquorice)};` : `background-color: ${colors.sugar};`}
          border-radius: 16px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          width: 100%;
          max-width: 320px;
          padding: ${spacing.regular};
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
        `}
      >
        {/* Icon */}
        <div
          css={css`
            width: 64px;
            height: 64px;
            border-radius: 16px;
            background: linear-gradient(135deg, ${colors.petrolBlue} 0%, ${colors.skyBlue} 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            margin-bottom: ${spacing.deci};
          `}
        >
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12 4L12 16M12 16L8 12M12 16L16 12"
              stroke={colors.sugar}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M4 20H20" stroke={colors.sugar} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* Title */}
        <h2
          id="update-dialog-title"
          css={css`
            ${fontStyles.shark};
            color: ${isDarkMode ? colors.sugar : colors.liquorice};
            margin: 0 0 ${spacing.centi} 0;
          `}
        >
          New Version Available
        </h2>

        {/* Description */}
        <p
          css={css`
            ${fontStyles.cat};
            color: ${isDarkMode ? colors.cloud : colors.tungsten};
            margin: 0 0 ${spacing.regular} 0;
          `}
        >
          A new version of Bloom is available. Please update for the best experience.
        </p>

        {/* Update Now Button */}
        <button
          ref={updateButtonRef}
          onClick={handleUpdateNow}
          css={css`
            width: 100%;
            padding: ${spacing.deci};
            background-color: ${colors.petrolBlue};
            color: ${colors.sugar};
            border: none;
            border-radius: 8px;
            cursor: pointer;
            ${fontStyles.fatCat};
            margin-bottom: ${spacing.centi};
            transition: background-color 0.2s ease;

            &:hover {
              background-color: ${lighten(0.05, colors.petrolBlue)};
            }

            &:active {
              background-color: ${colors.petrolBlue};
            }
          `}
        >
          Update Now
        </button>

        {/* Maybe Later Button */}
        <button
          onClick={onClose}
          css={css`
            width: 100%;
            padding: ${spacing.centi};
            background-color: transparent;
            color: ${isDarkMode ? colors.cloud : colors.tungsten};
            border: none;
            cursor: pointer;
            ${fontStyles.cat};
            transition: color 0.2s ease;

            &:hover {
              color: ${isDarkMode ? colors.sugar : colors.liquorice};
            }
          `}
        >
          Maybe Later
        </button>
      </div>
    </div>,
    document.body,
  );
};

export default AppUpdateDialog;
